import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type {
  ExternalSuiteProvider,
  SuiteFailureMode,
  SuiteAlertConfig,
  SuiteRunStartResult,
  TestSuiteDetail,
  TestSuiteListItem,
} from './types';

const json = { 'content-type': 'application/json' };

export function useTestSuites(enabled = true) {
  return useQuery({
    queryKey: ['test-suites'],
    enabled,
    staleTime: 5_000,
    queryFn: async (): Promise<TestSuiteListItem[]> => {
      const res = await fetch(`${API_URL}/test-suites`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load test suites');
      const body = (await res.json()) as { ok: boolean; data: TestSuiteListItem[] };
      return body.data;
    },
  });
}

export type RunnableExternalSuiteSummary = {
  id: string;
  name: string;
};

/**
 * Used by the Test Runs primary action. Keep this separate from the full suite
 * list so runner configuration never delays the runs table's first paint.
 */
export function useRunnableExternalTestSuites(enabled = true) {
  return useQuery({
    queryKey: ['test-suites', 'runnable-external'],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<RunnableExternalSuiteSummary[]> => {
      const res = await fetch(`${API_URL}/test-suites/runnable-external`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load runnable external test suites');
      const body = (await res.json()) as { ok: boolean; data: RunnableExternalSuiteSummary[] };
      return body.data;
    },
  });
}

export function useTestSuite(id: string | undefined) {
  return useQuery({
    queryKey: ['test-suite', id],
    enabled: !!id,
    staleTime: 10_000,
    queryFn: async (): Promise<TestSuiteDetail> => {
      const res = await fetch(`${API_URL}/test-suites/${id}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load test suite');
      const body = (await res.json()) as { ok: boolean; data: TestSuiteDetail };
      return body.data;
    },
  });
}

/** Update the suite-owned destinations used by both manual and scheduled runs. */
export function useUpdateSuiteAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, alertConfig }: { id: string; alertConfig: SuiteAlertConfig }) => {
      const res = await fetch(`${API_URL}/test-suites/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: json,
        body: JSON.stringify({ alertConfig }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save suite alerts');
      return res.json();
    },
    // Returned (not fire-and-forget): react-query keeps `isPending` true until
    // this resolves, so a caller awaiting `mutateAsync` sees the fresh
    // `alertConfig` land before its own pending state clears — otherwise a
    // fast Add/Remove could race the refetch and briefly show stale steps.
    onSuccess: (_data, variables) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['test-suite', variables.id] }),
        qc.invalidateQueries({ queryKey: ['test-suites'] }),
      ]),
  });
}

/**
 * The `external` half of a create request.
 *
 * Clone URLs, container images and command triples are server-derived. The
 * customer selects a linked repository, an uploaded bundle or an application
 * image source, depending on the source kind.
 */
export type CreateExternalSuiteInput =
  | {
      sourceKind: 'github';
      provider: ExternalSuiteProvider;
      /** A `property_github_repos` row id — never a URL. */
      propertyGithubRepoId: string;
      repoRef?: string | null;
      targetEnvironmentId?: string | null;
      credentialId?: string | null;
    }
  | {
      sourceKind: 'upload';
      provider: 'playwright';
      /** Upload runners still need a property for destination scoping. */
      propertyId: string;
      targetEnvironmentId?: string | null;
      credentialId?: string | null;
    }
  | {
      sourceKind: 'image';
      provider: 'playwright';
      propertyId: string;
      targetEnvironmentId: string;
      credentialId?: string | null;
    };

/** Upload one immutable Playwright bundle while exposing native XHR progress. */
export function uploadExternalTestBundle(
  suiteId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${API_URL}/test-suites/${suiteId}/bundle`);
    request.withCredentials = true;
    request.responseType = 'text';
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener('error', () => reject(new Error('The bundle upload failed')));
    request.addEventListener('abort', () => reject(new Error('The bundle upload was canceled')));
    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      let message = 'The bundle could not be uploaded';
      try {
        const body: unknown = JSON.parse(request.responseText);
        if (
          typeof body === 'object' &&
          body !== null &&
          'error' in body &&
          typeof body.error === 'string'
        ) {
          message = body.error;
        }
      } catch {
        // Keep the actionable fallback for a non-JSON proxy error.
      }
      reject(new Error(message));
    });
    const form = new FormData();
    form.append('bundle', file, file.name);
    request.send(form);
  });
}

export function useCreateTestSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string | null;
      kind?: 'native' | 'external';
      failureMode?: SuiteFailureMode;
      environmentId?: string | null;
      /** Required when `kind: 'external'`; rejected otherwise. */
      external?: CreateExternalSuiteInput;
    }): Promise<TestSuiteListItem> => {
      const res = await fetch(`${API_URL}/test-suites`, {
        method: 'POST',
        credentials: 'include',
        headers: json,
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create test suite');
      const body = (await res.json()) as { ok: boolean; data: TestSuiteListItem };
      return body.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['test-suites'] }),
  });
}

/** Idempotent: returns the existing default suite when the org already has one. */
export function useCreateDefaultTestSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ suite: TestSuiteListItem; created: boolean }> => {
      const res = await fetch(`${API_URL}/test-suites/default`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create the default suite');
      const body = (await res.json()) as {
        ok: boolean;
        data: { suite: TestSuiteListItem; created: boolean };
      };
      return body.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['test-suites'] }),
  });
}

export function useUpdateTestSuite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name?: string;
      description?: string | null;
      failureMode?: SuiteFailureMode;
      environmentId?: string | null;
    }) => {
      const res = await fetch(`${API_URL}/test-suites/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: json,
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update test suite');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test-suites'] });
      qc.invalidateQueries({ queryKey: ['test-suite', id] });
    },
  });
}

export function useArchiveTestSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/test-suites/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to archive test suite');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['test-suites'] }),
  });
}

/** Replace a suite's membership. `layers[i]` is layer i's ordered workflow ids;
 *  `staging` is the workflows in the suite but not in any layer (excluded from
 *  runs). */
export interface ReplaceSuiteMembersInput {
  layers: string[][];
  staging: string[];
}

export function useReplaceSuiteMembers(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ layers, staging }: ReplaceSuiteMembersInput) => {
      const res = await fetch(`${API_URL}/test-suites/${id}/members`, {
        method: 'PUT',
        credentials: 'include',
        headers: json,
        body: JSON.stringify({ layers, staging }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save suite layers');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test-suites'] });
      qc.invalidateQueries({ queryKey: ['test-suite', id] });
    },
  });
}

export function useRunTestSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      environmentId?: string | null;
      environmentMode?: 'mixed' | 'force';
      reEnableDegraded?: boolean;
      /** External suites only: run just these area paths instead of the whole suite. */
      externalAreas?: string[];
    }): Promise<SuiteRunStartResult> => {
      const runBody: {
        environmentId?: string;
        environmentMode?: 'mixed' | 'force';
        reEnableDegraded?: boolean;
        externalAreas?: string[];
      } = {};
      if (input.environmentMode) runBody.environmentMode = input.environmentMode;
      if (input.reEnableDegraded) runBody.reEnableDegraded = true;
      if (input.externalAreas) runBody.externalAreas = input.externalAreas;
      // Force mode pins to a specific env; mixed resolves per-flow so don't send one.
      if (input.environmentId && input.environmentMode !== 'mixed') {
        runBody.environmentId = input.environmentId;
      }
      const res = await fetch(`${API_URL}/test-suites/${input.id}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: json,
        body: JSON.stringify(runBody),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to run test suite');
      const body = (await res.json()) as { ok: boolean; data: SuiteRunStartResult };
      return body.data;
    },
    onSuccess: (_result, input) => {
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['test-suites'] });
      qc.invalidateQueries({ queryKey: ['test-suite', input.id] });
    },
  });
}
